import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
} from '@mui/material';
import {
  Lock as LockIcon,
  Star as StarIcon,
  Check as CheckIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useSubscription, PlanType } from '../../contexts/SubscriptionContext';

interface PaywallProps {
  open: boolean;
  onClose: () => void;
  feature: string;
  description: string;
  requiredPlan: PlanType;
  children?: React.ReactNode;
}

const planFeatures: Record<PlanType, string[]> = {
  free: ['3 reels per month', 'Basic templates', 'Standard quality'],
  basic: ['30 reels per month', 'All templates', 'HD quality', 'Basic analytics', 'BYOA option'],
  pro: ['100 reels per month', 'Advanced scheduling', 'Analytics insights', '4K quality', 'Priority support', 'BYOA option'],
  enterprise: ['200 reels per month', 'Team collaboration', 'White-label', 'API access', 'Dedicated support', 'BYOA option'],
  admin: ['Unlimited reels', 'All features', 'Admin access', 'Full platform control'],
};

const planPrices: Record<PlanType, number> = {
  free: 0,
  basic: 39,
  pro: 149,
  enterprise: 399,
  admin: 0,
};

const Paywall: React.FC<PaywallProps> = ({
  open,
  onClose,
  feature,
  description,
  requiredPlan,
  children,
}) => {
  const navigate = useNavigate();
  const { subscription, getUpgradeUrl } = useSubscription();

  const handleUpgrade = () => {
    onClose();
    navigate('/pricing');
  };

  const currentPlan = subscription?.plan.type || 'free';
  const isUpgrade = planPrices[requiredPlan] > planPrices[currentPlan];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          background: 'linear-gradient(135deg, rgba(26,26,46,0.98) 0%, rgba(26,26,46,1) 100%)',
        },
      }}
    >
      <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', stiffness: 200 }}
        >
          <Box
            sx={{
              display: 'inline-flex',
              p: 2,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.2) 0%, rgba(118, 75, 162, 0.2) 100%)',
              color: 'primary.main',
              mb: 2,
            }}
          >
            <LockIcon sx={{ fontSize: 40 }} />
          </Box>
        </motion.div>
        <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
          {isUpgrade ? 'Upgrade Required' : 'Premium Feature'}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {feature}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 0 }}>
        <Typography variant="body2" color="text.secondary" paragraph sx={{ textAlign: 'center' }}>
          {description}
        </Typography>

        {children}

        <Card
          sx={{
            mt: 3,
            background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
            border: '1px solid',
            borderColor: 'primary.main',
          }}
        >
          <CardContent>
            <Box display="flex" alignItems="center" gap={2} mb={2}>
              <StarIcon color="primary" />
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {requiredPlan.charAt(0).toUpperCase() + requiredPlan.slice(1)} Plan
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Starting at ${planPrices[requiredPlan]}/month
                </Typography>
              </Box>
              {requiredPlan === 'pro' && (
                <Chip
                  icon={<TrendingUpIcon />}
                  label="Most Popular"
                  color="primary"
                  size="small"
                  sx={{ ml: 'auto' }}
                />
              )}
            </Box>

            <List dense>
              {planFeatures[requiredPlan].map((planFeature, index) => (
                <ListItem key={index} disablePadding sx={{ py: 0.5 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <CheckIcon color="primary" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={planFeature}
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button onClick={onClose} color="inherit">
          Maybe Later
        </Button>
        <Button
          variant="contained"
          onClick={handleUpgrade}
          sx={{
            px: 4,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #7788ff 0%, #8659b8 100%)',
            },
          }}
        >
          {isUpgrade ? `Upgrade to ${requiredPlan}` : 'View Pricing'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default Paywall;